import type { LandingProductCopy } from './landingProductCopy';
import { assortmentLabels, timberPriceMarketingCopy } from './landingProductEvidenceData';
import type { LandingProductSnapshot } from './landingProductDescriptor.types';
import type {
  FmKeyFigure,
  FmKeyFiguresElement,
  FmProductSectionDescriptor,
} from '@/components/ui/archetypes';
import { productTimeSeriesChartSection } from '@/components/ui/archetypes';
import type { Language } from '@/i18n';

const SIMULATION_COMPARISON_COPY: Record<
  Language,
  {
    criteria: string;
    npv: string;
    cash: string;
    selection: string;
    previous: string;
    next: string;
    slide: string;
    intro: string;
  }
> = {
  fi: {
    criteria: 'Saman esimerkkimetsän mallinnetut tulokset',
    npv: 'Koko jakson arvo',
    cash: 'Nettokassavirta',
    selection: 'Vertailtava tulos',
    previous: 'Edellinen tulos',
    next: 'Seuraava tulos',
    slide: 'Tulos {index}/{count}',
    intro: 'Sama metsä. Neljä tavoitetta. Neljä erilaista suunnitelmaa.',
  },
  en: {
    criteria: 'Modelled outcomes for the same example forest',
    npv: 'Whole-period value',
    cash: 'Net cash flow',
    selection: 'Outcome to compare',
    previous: 'Previous outcome',
    next: 'Next outcome',
    slide: 'Outcome {index} of {count}',
    intro: 'One forest. Four goals. Four different plans.',
  },
  sv: {
    criteria: 'Modellerade resultat för samma exempelskog',
    npv: 'Värde för hela perioden',
    cash: 'Nettokassaflöde',
    selection: 'Resultat att jämföra',
    previous: 'Föregående resultat',
    next: 'Nästa resultat',
    slide: 'Resultat {index} av {count}',
    intro: 'Samma skog. Fyra mål. Fyra olika planer.',
  },
  es: {
    criteria: 'Resultados modelizados para el mismo bosque de ejemplo',
    npv: 'Valor de todo el periodo',
    cash: 'Flujo de caja neto',
    selection: 'Resultado que comparar',
    previous: 'Resultado anterior',
    next: 'Resultado siguiente',
    slide: 'Resultado {index} de {count}',
    intro: 'Un bosque. Cuatro objetivos. Cuatro planes distintos.',
  },
};

export function timberPriceSection(
  copy: LandingProductCopy,
  snapshot: Readonly<LandingProductSnapshot>,
): FmProductSectionDescriptor {
  const prices = snapshot.timberPrices;
  const marketing = timberPriceMarketingCopy(snapshot.locale);
  const chartPoints = prices.data?.points.slice(-157) ?? [];
  if (prices.loading) {
    return {
      id: 'landing-timber-prices',
      capability: 'section',
      role: 'lead',
      title: marketing.title,
      section: {
        id: 'landing-timber-prices-story',
        arrangement: 'prose',
        state: { status: 'loading', label: copy.state.loading },
      },
    };
  }
  if (prices.error || !prices.data || prices.data.points.length === 0) {
    return {
      id: 'landing-timber-prices',
      capability: 'section',
      role: 'lead',
      title: marketing.title,
      section: {
        id: 'landing-timber-prices-story',
        arrangement: 'prose',
        state: {
          status: 'empty',
          label: copy.state.empty,
          explanation: copy.labels.roughValueUnavailable,
        },
      },
    };
  }
  const labels = assortmentLabels(snapshot.locale);
  return productTimeSeriesChartSection({
    id: 'landing-timber-prices',
    sectionId: 'landing-timber-prices-story',
    title: marketing.title,
    summary: marketing.summary,
    locale: snapshot.locale,
    chartTitle: `${prices.data.regionLabel} · €/m³`,
    caption: marketing.contextSuffix,
    unit: '€/m³',
    takeaway: marketing.takeaway,
    source: copy.labels.roughValuePriceSourceName,
    xKey: 'week',
    series: [
      { code: 'N1', label: labels.pine, dataKey: 'N1', tone: 'teal', role: 'result' },
      { code: 'N4', label: labels.pulp, dataKey: 'N4', tone: 'amber', role: 'reference' },
    ],
    data: chartPoints,
    testId: 'landing-timber-prices-chart',
  });
}

type SimulationAlternative = Extract<
  LandingProductSnapshot['heroForecast'],
  { status: 'ready' }
>['alternatives'][number];

/**
 * One outcome's key figures: the four plans in descending order of that
 * outcome, an unserved value explicit rather than ranked.
 */
function outcomeFigures(
  plans: readonly { label: string; focus: string; result: SimulationAlternative | undefined }[],
  value: (result: SimulationAlternative) => number | null,
  unit: string,
  locale: Language,
  unavailableReason: string,
): FmKeyFiguresElement {
  const format = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const ranked = plans
    .map((plan) => ({ plan, served: plan.result ? value(plan.result) : null }))
    .sort((left, right) => (right.served ?? -Infinity) - (left.served ?? -Infinity));
  const figures = ranked.map(({ plan, served }): FmKeyFigure =>
    served === null
      ? { label: plan.label, value: null, unavailableReason }
      : { label: plan.label, value: format.format(Math.round(served)), unit, footnote: plan.focus },
  );
  const [first, ...rest] = figures;
  return { element: 'keyFigures', figures: [first, ...rest] };
}

export function simulationComparisonSection(
  copy: LandingProductCopy,
  snapshot: Readonly<LandingProductSnapshot>,
): FmProductSectionDescriptor {
  const simulation = copy.valueStreams.simulation;
  const forecast = snapshot.heroForecast;
  const base = {
    id: 'landing-simulation-comparison',
    capability: 'section' as const,
    role: 'lead' as const,
    title: simulation.title,
  };
  if (forecast.status !== 'ready') {
    return {
      ...base,
      section: {
        id: 'landing-simulation-comparison-story',
        arrangement: 'carousel',
        state: {
          status: 'empty',
          label: copy.state.empty,
          explanation: copy.labels.roughValueUnavailable,
        },
      },
    };
  }
  const ids = ['whole-period-value', 'near-term-revenue', 'avoid-clearcuts', 'tapio'] as const;
  const text = SIMULATION_COMPARISON_COPY[snapshot.locale];
  const plans = ids.map((id, index) => ({
    label: simulation.lenses[index].title,
    focus: simulation.lenses[index].focus,
    result: forecast.alternatives.find((alternative) => alternative.id === id),
  }));
  const reason = copy.labels.roughValueUnavailable;
  return {
    ...base,
    section: {
      id: 'landing-simulation-comparison-story',
      arrangement: 'carousel',
      state: { status: 'ready' },
      labels: {
        carousel: text.selection,
        previous: text.previous,
        next: text.next,
        slide: text.slide,
      },
      slots: { lead: [{ element: 'copy', lede: text.intro, sourceNote: text.criteria }] },
      slides: [
        {
          key: 'npv',
          label: text.npv,
          elements: [
            outcomeFigures(plans, (result) => result.npvPerHectareEur, '€/ha', snapshot.locale, reason),
          ],
        },
        {
          key: 'cash',
          label: text.cash,
          elements: [
            outcomeFigures(plans, (result) => result.netCashEur, '€', snapshot.locale, reason),
          ],
        },
      ],
    },
  };
}
